import { cn } from "@/lib/utils";
import "@assets/css/bootstrap-grid.css";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint, getOptions } from "@services/api.services";
import axiosInstance from "@services/index.services";
import { METHODS } from "@services/interface.service";
import { useMutation } from "@tanstack/react-query";
import { ResponseSkeleton } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  isNullCheck,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  ContactUsFormData,
  ContactUsMasterClassData,
  ContactUsMasterClassSchema,
  ContactUsSchema,
  enrollmentSchema,
  singleStudentEnrollmentFormData,
  singleStudentEnrollmentSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import {
  ContactUsData,
  EnrollmentData,
  SessionData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useSelectionStore } from "@utils/states/index.state";

import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router";

const EnrollmentForm = ({ refetch }: { refetch: () => void }) => {
  const { searchParams } = useHandleAction({});

  const {
    handleSubmit,
    control,
    reset,

    setValue,
    formState: { errors },
  } = useForm<singleStudentEnrollmentFormData>({
    mode: "onChange",
    resolver: zodResolver(singleStudentEnrollmentSchema),
  });
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<EnrollmentData>
  >({
    endpoint: endpoint["enrollment"],
    id: searchParams.get("modal-item") || "",
    key: "enrollment" + searchParams.get("modal-item"),
    enabled: searchParams.get("modal-item") ? true : false,
  });
  console.log("data", data);
  /* set default value */
  useEffect(() => {
    if (data?.data) {
      reset({
        ...data.data,
        full_name: isNullCheck(data?.data?.name) as string | undefined,
      });
    }
  }, [data, reset]);
  console.log("errors", errors);
  const { mutate, isPending } = usePostData();
  const { handleAction } = useHandleAction({
    endpoint: endpoint["enrollmenttostudent"],
  });
  const handleMutate = (data: any) => {
    data = {
      ...data,
      phone: data?.phone_number.replace(/\+/g, ""),
      course_batch: data?.course_batch?.map((item: any) => item.value) || [],
    };
    delete data.phone_number;
    return {
      data,
      endpoint: endpoint["enrollmenttostudent"],
      method: METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      handleAction({ type: "close" });
      refetch();
      showToast({
        description: `Student has been  ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();

  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const onSubmit: SubmitHandler<singleStudentEnrollmentFormData> = (data) => {
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "full_name",
      label: "Full Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Fullname",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone_number",
      label: "Phone Number",
      colSpan: 12,
      type: "tel",
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "course_batch",
      label: "Select Course & Batch",
      colSpan: 12,
      type: "async",
      placeholder: "Select Course & Batch",
      control: control,
      setValue: setValue,
      endpoint: data?.data?.id
        ? endpoint["enrollment-course-batch"]
        : endpoint["course-batch"],
      filter: data?.data?.id && { id: data?.data?.id },
      format: { key: "name", value: "id", mutate: "name" },
      settings: { add: false, update: false, remove: false, debounce: 1000 },
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "professional_image",
      label: "Upload Image",
      colSpan: 12,
      type: "file",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `Convert to Student`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",

      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];
  if (fetchPending) {
    return <ResponseSkeleton />;
  }
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            ),
        )}
      </form>
    </>
  );
};

const SessionInquiryForm = ({ refetch }: { refetch: () => void }) => {
  const { searchParams } = useHandleAction({});

  const {
    control,
    reset,

    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<SessionData>
  >({
    endpoint: endpoint["email-sent"],
    id: searchParams.get("modal-item") || "",
    key: "email-sent" + searchParams.get("modal-item"),
    enabled: searchParams.get("modal-item") ? true : false,
  });
  /* set default value */
  useEffect(() => {
    if (data?.data) {
      reset({
        ...data.data,
        full_name: isNullCheck(data?.data?.name) as string | undefined,
        recipient: isNullCheck(data?.data?.recipient),
        course: isNullCheck(data?.data?.course?.title),
      });
    }
  }, [data, reset]);
  console.log("errors", errors);

  const formSchema: FormField[] = [
    {
      name: "full_name",
      label: "Full Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Fullname",
      control: control,
      setValue: setValue,
      disabled: true,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "recipient",
      label: "Email",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "course",
      label: "Course",
      disabled: true,
      colSpan: 12,
      type: "text",
      placeholder: "Enter Course",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
  ];
  if (fetchPending) {
    return <ResponseSkeleton />;
  }
  return (
    <>
      <form className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            ),
        )}
      </form>
    </>
  );
};
export default EnrollmentForm;

type StudentConversionFormProps = {
  studentData: any[]; // ideally, type this!
  refetch: () => void; // function to refetch data after submission
  toggleStudentConversion: () => void; // optional function to toggle conversion
};

const StudentConversionForm: React.FC<StudentConversionFormProps> = ({
  studentData,
  refetch,
  toggleStudentConversion,
}) => {
  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(enrollmentSchema), // Uncomment when you have a schema
    defaultValues: {
      students: studentData.map((student) => ({
        full_name: student.name || "",
        email: student.email || "",
        phone_number: student.phone_number || "",
        course_batch: [],
        image: undefined,
      })),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "students",
  });

  // Log errors whenever they change
  useEffect(() => {
    console.log("Current form errors:", errors);
  }, [errors]);

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "full_name",
        label: "Full Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Full Name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
        rules: { required: "Full name is required" }, // Add validation rules
      },
      {
        name: "email",
        label: "Email",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Email",
        control: control,
        setValue: setValue,
        errors,
        visable: true,
        rules: {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          },
        },
      },
      {
        name: "phone_number",
        label: "Phone Number",
        colSpan: 12,
        type: "tel",
        placeholder: "Enter Phone Number",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
        rules: {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Phone number must be at least 10 digits",
          },
        },
      },
      {
        name: "course_batch",
        label: "Course & Batch",
        colSpan: 12,
        isMulti: true,
        type: "async",
        placeholder: "Select Course & Batch",
        control: control,
        setValue: setValue,
        errors,
        endpoint: endpoint["course-batch"],
        format: { key: "name", value: "id", mutate: "name" },
        settings: { add: false, update: false, remove: false, debounce: 1000 },
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
      },
      {
        name: "professional_image",
        label: "Image",
        colSpan: 12,
        type: "file",
        placeholder: "Show Image",
        control: control,
        setValue: setValue,
        errors,
        visable: true,
        rules: {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          },
        },
      },
    ],
    [control, errors, setValue],
  );
  const { clearSelection } = useSelectionStore();
  const handleMutate = (data: any) => {
    const { students } = data;

    data = students.map((student: any) => ({
      ...student,
      course_batch: student.course_batch.map((item: any) => item.value),
      phone: student?.phone_number,
    }));
    delete data.phone_number;
    return {
      data,
      endpoint: endpoint["enrollmenttostudent"],
      method: METHODS.POST,
    };
  };

  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      toggleStudentConversion();
      refetch();
      clearSelection();
      showToast({
        description: `Student has been ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();

  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorsMessage = error?.data?.data ?? {};
    console.log("Error Message ", errorsMessage);
    setIsFormPending(false);
    if (Array.isArray(errorsMessage)) {
      errorsMessage.forEach((error) => {
        Object.entries(error).forEach(([key, value]) => {
          showToast({
            description: `Failed to create ${key}: ${value}`,
            type: "error",
            title: "Error",
          });
        });
      });
    } else {
      Object.entries(errorsMessage).forEach(([key, value]) => {
        showToast({
          description: `Failed to create ${key}: ${value}`,
          type: "error",
          title: "Error",
        });
      });
    }
    // throwErrorAlert(error);
  };
  const { mutate } = useMutation({
    mutationKey: ["enrollmenttostudent"],
    mutationFn: handleSingleEnrollment,
  });
  const onSubmit = (data: any) => {
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div></div>
        <Button type="submit" className="w-fit">
          Submit All
        </Button>
      </div>

      {fields.map((item, index) => (
        <div
          key={item.id}
          className="border p-4 rounded mb-4  overflow-auto flex flex-col gap-4"
        >
          <h3 className="font-semibold mb-2">
            Student {index + 1}: {studentData[index]?.name}
          </h3>
          <div className="flex gap-2 max-md:flex-col items-start">
            {formSchema.map((field, fIndex) => (
              <div key={fIndex} className="flex-1">
                {getInputField({
                  ...field,
                  name: `students.${index}.${field.name}`,
                  control,
                  setValue,
                  register,
                  errors,
                })}

                {/* Display errors for each field */}
                {field.name &&
                  errors?.students?.[index]?.[
                    field.name as keyof (typeof errors.students)[typeof index]
                  ] && (
                    <p className="text-red-500 text-sm mt-1">
                      {
                        (
                          errors.students[index][
                            field.name as keyof (typeof errors.students)[typeof index]
                          ] as any
                        )?.message
                      }
                    </p>
                  )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </form>
  );
};
const ContactUsViewForm: React.FC = () => {
  const { searchParams } = useHandleAction({});

  const {
    getValues,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactUsFormData>({
    mode: "onChange",
    resolver: zodResolver(ContactUsSchema),
  });
  const drawerItem = searchParams.get("drawer-item");
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<ContactUsData>
  >({
    endpoint: endpoint["contactus"],
    id: drawerItem || "",
    key: "contactus" + drawerItem,
    enabled: drawerItem ? true : false,
  });

  /* set default value */
  useEffect(() => {
    if (data?.data) {
      reset({
        full_name: isNullCheck(data?.data?.full_name) as string | undefined,
        email: isNullCheck(data?.data?.email) as string | undefined,
        phone_number: isNullCheck(data?.data?.phone_number) as
          | string
          | undefined,
        address: isNullCheck(data?.data?.address) as string | undefined,
        subject: isNullCheck(data?.data?.subjects[0]?.name) as
          | string
          | undefined,
        query: isNullCheck(data?.data?.message) as string | undefined,
      });
    }
  }, [data, reset]);

  const formSchema: FormField[] = [
    {
      name: "full_name",
      label: "Full Name",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Fullname",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone_number",
      label: "Phone Number",
      colSpan: 12,
      type: "tel",
      disabled: true,
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "subject",
      label: "Subject",
      disabled: true,
      type: "component",
      errors,
      component: (
        <>
          {
            <div className="cursor-not-allowed flex items-center capitalize gap-2">
              <span
                className={cn(
                  "w-[10px] block h-[10px] rounded-full ",
                  {
                    others: "bg-[#f59e0b]",
                    certificate: "bg-[#1DA077]",
                  }[
                    // @ts-ignore
                    getValues("subject")
                  ] || "bg-[#C33241]",
                )}
              ></span>
              {getValues("subject")}
            </div>
          }
        </>
      ),
    },
    {
      name: "address",
      label: "Address",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: "Enter Address",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: true,
    },

    {
      name: "query",
      label: "Message",
      colSpan: 12,
      type: "textarea",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
      disabled: true,
    },
  ];

  if (fetchPending) {
    return <ResponseSkeleton />;
  }
  return (
    <>
      <div className="flex flex-col gap-2">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div key={index} className={``}>
                {getInputField({ ...field })}
              </div>
            ),
        )}
      </div>
    </>
  );
};

const EnrollmentViewForm: React.FC = () => {
  const { searchParams } = useHandleAction({});

  const {
    getValues,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactUsFormData>({
    mode: "onChange",
    resolver: zodResolver(ContactUsSchema),
  });
  const drawerItem = searchParams.get("drawer-item");
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<EnrollmentData>
  >({
    endpoint: endpoint["enrollment"],
    id: drawerItem || "",
    key: "enrollment" + drawerItem,
    enabled: drawerItem ? true : false,
  });
  const { type } = useParams();
  console.log("data", type);
  /* set default value */
  useEffect(() => {
    if (data?.data) {
      reset({
        ...data?.data,
        course: data?.data?.course?.map((item) => item.title),
        full_name: isNullCheck(data?.data?.name) as string | undefined,
        email: isNullCheck(data?.data?.email) as string | undefined,
        phone_number: isNullCheck(data?.data?.phone_number) as
          | string
          | undefined,
        address: isNullCheck(data?.data?.address) as string | undefined,
        subject: isNullCheck(data?.data?.subject) as string | undefined,
        query: isNullCheck(data?.data?.query) as string | undefined,
      });
    }
  }, [data, reset]);

  const formSchema: FormField[] = [
    {
      name: "full_name",
      label: "Full Name",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Fullname",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone_number",
      label: "Phone Number",
      colSpan: 12,
      type: "tel",
      disabled: true,
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "address",
      label: "Address",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: "Enter Address",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: true,
    },
    {
      name: "course",
      label: "Course",
      disabled: true,
      type: "component",
      errors,
      component: (
        <div className="flex gap-1 flex-wrap w-[200px]">
          {getValues("course") &&
            getValues("course").length > 0 &&
            getValues("course").map((e: any) => (
              <Badge
                variant={"secondary"}
                className="hover:cursor-not-allowed text-yellow-500"
              >
                {e ?? "N/a"}
              </Badge>
            ))}
        </div>
      ),
    },
    {
      name: "education_level",
      label: "Education Level",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: " Education Level",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: type !== "masterclass-enrollment" ? true : false,
    },
    {
      name: "learning_format	",
      label: "Learning Format",
      disabled: true,
      type: "component",
      errors,
      visable: type !== "masterclass-enrollment" ? true : false,
      component: (
        <>
          {
            <div className="cursor-not-allowed flex items-center capitalize gap-2">
              <span
                className={cn(
                  "w-[10px] block h-[10px] rounded-full ",
                  {
                    others: "bg-[#f59e0b]",
                    certificate: "bg-[#1DA077]",
                  }[
                    getValues("learning_format") as
                      | "others"
                      | "certificate"
                      | string
                  ] || "bg-[#C33241]",
                )}
              ></span>
              {getValues("learning_format")}
            </div>
          }
        </>
      ),
    },

    {
      name: "has_laptop",
      label: "Has Laptop",
      colSpan: 12,
      type: "component",
      component: <>{getValues("has_laptop") ? "Yes" : "No"}</>,
      disabled: true,
      placeholder: "Enter Address",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: type !== "masterclass-enrollment" ? true : false,
    },
  ];

  if (fetchPending) {
    return <ResponseSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div key={index} className={``}>
                {getInputField({ ...field })}
              </div>
            ),
        )}
      </div>
    </>
  );
};

const MasterClassEnrollViewForm: React.FC = () => {
  const { searchParams } = useHandleAction({});

  const {
    getValues,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactUsMasterClassData>({
    mode: "onChange",
    resolver: zodResolver(ContactUsMasterClassSchema),
  });

  const drawerItem = searchParams.get("drawer-item");
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<EnrollmentData>
  >({
    endpoint: endpoint["enrollment"],
    id: drawerItem || "",
    key: "enrollment" + drawerItem,
    enabled: drawerItem ? true : false,
  });
  const { type } = useParams();
  console.log("data", type);
  /* set default value */
  useEffect(() => {
    if (data?.data) {
      reset({
        ...data?.data,
        course: data?.data?.course?.map((item) => item.title),
        full_name: isNullCheck(data?.data?.name) as string | undefined,
        email: isNullCheck(data?.data?.email) as string | undefined,
        phone_number: isNullCheck(data?.data?.phone_number) as
          | string
          | undefined,
        address: isNullCheck(data?.data?.address) as string | undefined,
        profession: isNullCheck(data?.data?.profession) as string | undefined,
        query: isNullCheck(data?.data?.query) as string | undefined,
      });
    }
  }, [data, reset]);

  const formSchema: FormField[] = [
    {
      name: "full_name",
      label: "Full Name",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Fullname",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      disabled: true,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone_number",
      label: "Phone Number",
      colSpan: 12,
      type: "tel",
      disabled: true,
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,

      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "address",
      label: "Address",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: "Enter Address",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: true,
    },
    {
      name: "profession",
      label: "Profession",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: "Enter Profession",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: true,
    },
    {
      name: "course",
      label: "Course",
      disabled: true,
      type: "component",
      errors,
      component: (
        <div className="flex gap-1 flex-wrap w-[200px]">
          {getValues("course") &&
            getValues("course").length > 0 &&
            getValues("course").map((e: any) => (
              <Badge
                variant={"secondary"}
                className="hover:cursor-not-allowed text-yellow-500"
              >
                {e ?? "N/a"}
              </Badge>
            ))}
        </div>
      ),
    },
    {
      name: "education_level",
      label: "Education Level",
      colSpan: 12,
      type: "text",
      disabled: true,
      placeholder: " Education Level",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: type !== "masterclass-enrollment" ? true : false,
    },
    {
      name: "learning_format	",
      label: "Learning Format",
      disabled: true,
      type: "component",
      errors,
      visable: type !== "masterclass-enrollment" ? true : false,
      component: (
        <>
          {
            <div className="cursor-not-allowed flex items-center capitalize gap-2">
              <span
                className={cn(
                  "w-[10px] block h-[10px] rounded-full ",
                  {
                    others: "bg-[#f59e0b]",
                    certificate: "bg-[#1DA077]",
                  }[
                    getValues("learning_format") as
                      | "others"
                      | "certificate"
                      | string
                  ] || "bg-[#C33241]",
                )}
              ></span>
              {getValues("learning_format")}
            </div>
          }
        </>
      ),
    },

    {
      name: "has_laptop",
      label: "Has Laptop",
      colSpan: 12,
      type: "component",
      component: <>{getValues("has_laptop") ? "Yes" : "No"}</>,
      disabled: true,
      placeholder: "Enter Address",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full hover:cursor-not-allowed w-full`,
      visable: type !== "masterclass-enrollment" ? true : false,
    },

    {
      name: "query",
      label: "Message",
      colSpan: 12,
      type: "textarea",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] cursor-not-allowed min-w-full w-full`,
      visable: true,
      disabled: true,
    },
  ];

  if (fetchPending) {
    return <ResponseSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div key={index} className={``}>
                {getInputField({ ...field })}
              </div>
            ),
        )}
      </div>
    </>
  );
};

export {
  ContactUsViewForm,
  EnrollmentForm,
  EnrollmentViewForm,
  SessionInquiryForm,
  StudentConversionForm,
  MasterClassEnrollViewForm,
};
const handleSingleEnrollment = async ({ data }: { data: any }) => {
  return axiosInstance.post("/enrollmenttostudent/", data, getOptions());
};
